// Shared by the bulk-activation edge function (Deno) and the portal upload preview (Nuxt),
// so it must stay free of runtime-specific imports.

export type FamilyInputRow = {
    index: number
    policyHolder: string
    fullName: string
    hasPhone: boolean
}

export type FamilyGroup = {
    key: string
    policyHolder: string
    indices: number[]
    headCandidateIndices: number[]
    dependentIndices: number[]
    ambiguousHead: boolean
}

export function normalizeName(value: string | null | undefined): string {
    return (value ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toUpperCase()
        .replace(/\s+/g, ' ')
        .trim()
}

export function matchesPolicyHolder(fullName: string, policyHolder: string): boolean {
    const key = normalizeName(policyHolder)
    const name = normalizeName(fullName)
    if (!key || !name) return false
    if (name === key) return true

    const parts = name.split(' ')
    return parts.length > 2 && `${parts[0]} ${parts[parts.length - 1]}` === key
}

export function groupFamilies(rows: FamilyInputRow[]): FamilyGroup[] {
    const groups = new Map<string, FamilyGroup & { matching: number[]; otherPhones: number[] }>()

    for (const row of rows) {
        const key = normalizeName(row.policyHolder)
        if (!key) continue

        let group = groups.get(key)
        if (!group) {
            group = {
                key,
                policyHolder: row.policyHolder.trim(),
                indices: [],
                headCandidateIndices: [],
                dependentIndices: [],
                ambiguousHead: false,
                matching: [],
                otherPhones: [],
            }
            groups.set(key, group)
        }

        group.indices.push(row.index)
        if (!row.hasPhone) {
            group.dependentIndices.push(row.index)
        } else if (matchesPolicyHolder(row.fullName, key)) {
            group.matching.push(row.index)
        } else {
            group.otherPhones.push(row.index)
        }
    }

    return Array.from(groups.values()).map(({ matching, otherPhones, ...group }) => ({
        ...group,
        headCandidateIndices: [...matching, ...otherPhones],
        ambiguousHead: matching.length > 1,
    }))
}

export function summarizeFamilies(groups: FamilyGroup[]) {
    return {
        families: groups.length,
        largest: groups.reduce((max, g) => Math.max(max, g.indices.length), 0),
        withoutPhone: groups.filter(g => !g.headCandidateIndices.length).length,
    }
}

export function dependentKey(parentUserId: string, firstName: string, lastName: string | null | undefined, birthDate: string): string {
    return [parentUserId, normalizeName(firstName), normalizeName(lastName), birthDate].join('|')
}
